import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  analyticsApi,
  parseEvidenceIds,
  type AnalyticsProblemEntryDto,
} from '../../api/analytics';
import { reportsApi } from '../../api/reports';
import { invoicesApi } from '../../api/invoices';
import { workspacesApi, getWorkspaceId, type WorkspaceInfo } from '../../api/workspaces';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { type FilterCondition, type FilterSpec } from '../../api/insights';
import MetricTile from '../../components/analytics/MetricTile';
import AgingBar from '../../components/analytics/AgingBar';
import DrillTable from '../../components/analytics/DrillTable';
import ProblemsPanel from '../../components/analytics/ProblemsPanel';
import SuggestionStrip from '../../components/analytics/SuggestionStrip';
import NlSearchBox from '../../components/analytics/NlSearchBox';
import ExplainPanel from '../../components/analytics/ExplainPanel';
import { formatMoney } from '../../utils/formatMoney';

/**
 * Analytics dashboard — the drill-down surface.
 * L0 overview → clickable tiles/aging/problems → customer → invoice →
 * possible matching payments. Every click is deterministic (no LLM in the
 * navigation path); Mistral only powers the NL search box and "Paaiškinti".
 */

type DrillView =
  | { kind: 'overview' }
  | { kind: 'customers'; type: 'receivable' | 'payable'; bucketDaysFrom?: number; filters?: FilterCondition[] }
  | { kind: 'customer'; personId: number; personName?: string }
  | { kind: 'invoice'; invoiceId: number }
  | { kind: 'findings'; scanner?: string }
  | { kind: 'bank' }
  | { kind: 'vat' };

const todayStr = () => new Date().toISOString().slice(0, 10);

/** Map an aging bucket daysFrom to the person-row field holding its amount. */
function bucketField(daysFrom: number): string {
  if (daysFrom <= 0) return 'current';
  if (daysFrom <= 30) return 'b1';
  if (daysFrom <= 60) return 'b31';
  if (daysFrom <= 90) return 'b61';
  return 'b90';
}

function viewToParams(view: DrillView): Record<string, string> {
  switch (view.kind) {
    case 'overview': return {};
    case 'customers': return {
      view: 'customers', type: view.type,
      ...(view.bucketDaysFrom !== undefined ? { bucket: String(view.bucketDaysFrom) } : {}),
    };
    case 'customer': return { view: 'customer', person: String(view.personId), ...(view.personName ? { name: view.personName } : {}) };
    case 'invoice': return { view: 'invoice', invoice: String(view.invoiceId) };
    case 'findings': return { view: 'findings', ...(view.scanner ? { scanner: view.scanner } : {}) };
    case 'bank': return { view: 'bank' };
    case 'vat': return { view: 'vat' };
  }
}

function paramsToView(params: Record<string, string>): DrillView {
  switch (params.view) {
    case 'customers': return { kind: 'customers', type: params.type === 'payable' ? 'payable' : 'receivable', bucketDaysFrom: params.bucket ? Number(params.bucket) : undefined };
    case 'customer': return params.person ? { kind: 'customer', personId: Number(params.person), personName: params.name } : { kind: 'overview' };
    case 'invoice': return params.invoice ? { kind: 'invoice', invoiceId: Number(params.invoice) } : { kind: 'overview' };
    case 'findings': return { kind: 'findings', scanner: params.scanner };
    case 'bank': return { kind: 'bank' };
    case 'vat': return { kind: 'vat' };
    default: return { kind: 'overview' };
  }
}

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enableAnalytics, loading: flagsLoading } = useFeatureFlags();
  const workspaceId = getWorkspaceId();

  const [stack, setStack] = useState<DrillView[]>([paramsToView(Object.fromEntries(searchParams.entries()))]);
  const view: DrillView = stack.at(-1) ?? { kind: 'overview' };

  const push = (next: DrillView) => setStack(prev => [...prev, next]);
  const popTo = (index: number) => setStack(prev => prev.slice(0, index + 1));

  // Keep the URL shareable: reflect the current drill view (replace, no history spam)
  useEffect(() => {
    const params = viewToParams(view);
    setSearchParams(params, { replace: true });
  }, [view, setSearchParams]);

  const { isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['analytics-overview', workspaceId],
    queryFn: analyticsApi.overview,
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

  const { data: workspaces } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
  });
  const activeWorkspace: WorkspaceInfo | undefined = workspaces?.find(w => w.isActive);
  const dataAsOf = activeWorkspace?.lastSyncedAtUtc ?? null;

  // Per-workspace AI consent: NL search + explain render only when egress is allowed
  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status', workspaceId],
    queryFn: () => analyticsApi.aiStatus(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const aiEnabled = !!aiStatus?.aiEnabled;

  const handleNlSpec = (spec: FilterSpec | null, rawText: string) => {
    if (spec?.view === 'customers') {
      const filters = (spec.filters ?? []).map(f => ({ ...f, key: f.key === 'totalOutstanding' ? 'total' : f.key }));
      push({ kind: 'customers', type: 'receivable', filters: filters.length ? filters : undefined });
    } else if (spec?.view === 'payments') {
      push({ kind: 'findings', scanner: 'UnmatchedPayments' });
    } else if (spec?.view === 'findings') {
      push({ kind: 'findings' });
    } else {
      // Fallback: treat the text as a search over the debtors list
      push({ kind: 'customers', type: 'receivable', filters: [{ key: 'name', op: 'contains', value: rawText }] });
    }
  };

  if (!flagsLoading && !enableAnalytics) {
    return <NavigateHome />;
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header: title, data-as-of, NL search (AI-gated) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-900">{t('analytics.title')}</h1>
        <span className="text-xs text-gray-400">
          {dataAsOf
            ? t('analytics.dataAsOf', { time: new Date(dataAsOf).toLocaleString('lt-LT') })
            : t('analytics.neverSynced')}
        </span>
        <div className="flex-1" />
        {aiEnabled && view.kind === 'overview' && <NlSearchBox onSpec={handleNlSpec} />}
      </div>

      {/* Breadcrumbs (drill path) */}
      {stack.length > 1 && (
        <div className="flex flex-wrap items-center gap-1 mb-4 text-sm">
          {stack.map((item, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <span className="text-gray-300">›</span>}
              <button
                onClick={() => popTo(i)}
                className={`px-2 py-0.5 rounded hover:bg-gray-100 ${i === stack.length - 1 ? 'font-medium text-gray-900' : 'text-gray-500'}`}
              >
                {t(`analytics.view.${item.kind === 'customers' ? 'customers' : item.kind}`)}
                {item.kind === 'customer' && item.personName ? `: ${item.personName}` : ''}
              </button>
            </span>
          ))}
          <button onClick={() => setStack([{ kind: 'overview' }])} className="ml-2 text-xs text-gray-400 hover:text-gray-600 underline">
            {t('analytics.back')}
          </button>
        </div>
      )}

      {view.kind === 'overview' && (
        <OverviewView
          loading={overviewLoading}
          error={overviewError instanceof Error ? overviewError.message : ''}
          aiEnabled={aiEnabled}
          onDrill={push}
        />
      )}
      {view.kind === 'customers' && <CustomersView type={view.type} bucketDaysFrom={view.bucketDaysFrom} filters={view.filters} onDrill={push} />}
      {view.kind === 'customer' && <CustomerView personId={view.personId} personName={view.personName} onDrill={push} />}
      {view.kind === 'invoice' && <InvoiceView invoiceId={view.invoiceId} />}
      {view.kind === 'findings' && <FindingsView scanner={view.scanner} onDrill={push} />}
      {view.kind === 'bank' && <BankView overviewLoading={overviewLoading} onOpenBanking={() => navigate('/banking')} />}
      {view.kind === 'vat' && <VatView onOpenVat={() => navigate('/reports/vat')} />}
    </div>
  );
}

function NavigateHome() {
  const navigate = useNavigate();
  useEffect(() => { navigate('/', { replace: true }); }, [navigate]);
  return null;
}

// ─── L0: Overview ───────────────────────────────────────────────────────────

interface OverviewViewProps {
  loading: boolean;
  error: string;
  aiEnabled: boolean;
  onDrill: (view: DrillView) => void;
}

function OverviewView({ loading, error, aiEnabled, onDrill }: OverviewViewProps) {
  const { t } = useTranslation();
  const { data: overview } = useQuery({ queryKey: ['analytics-overview', getWorkspaceId()], queryFn: analyticsApi.overview, staleTime: 60_000 });

  if (loading && !overview) {
    return <div className="py-16 text-center text-gray-400">{t('analytics.loading')}</div>;
  }
  if (error) {
    return <div className="py-8 text-center text-red-600">{error}</div>;
  }
  if (!overview) return null;

  const overdueTone = overview.receivables.overdueSharePercent >= 50 ? 'critical' : overview.receivables.overdueTotal > 0 ? 'warning' : 'positive';

  return (
    <div className="space-y-6">
      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        <MetricTile
          label={t('analytics.tile.receivables')}
          value={formatMoney(overview.receivables.totalOutstanding)}
          sub={t('analytics.tile.debtors', { count: overview.receivables.personCount })}
          onClick={() => onDrill({ kind: 'customers', type: 'receivable' })}
        />
        <MetricTile
          label={t('analytics.tile.overdue')}
          value={formatMoney(overview.receivables.overdueTotal)}
          sub={t('analytics.tile.overdueShare', { value: formatMoney(overview.receivables.overdueSharePercent, false) + ' %' })}
          tone={overdueTone}
          onClick={() => onDrill({ kind: 'customers', type: 'receivable', bucketDaysFrom: 1 })}
        />
        <MetricTile
          label={t('analytics.tile.payables')}
          value={formatMoney(overview.payables.totalOutstanding)}
          sub={t('analytics.tile.debtors', { count: overview.payables.personCount })}
          onClick={() => onDrill({ kind: 'customers', type: 'payable' })}
        />
        <MetricTile
          label={t('analytics.tile.cash')}
          value={formatMoney(overview.cash.total)}
          sub={overview.cash.accounts.length > 0 ? String(overview.cash.accounts[0]!.accountNumber) : ''}
          onClick={() => onDrill({ kind: 'bank' })}
        />
        <MetricTile
          label={t('analytics.tile.vat')}
          value={formatMoney(overview.vat.netVatPayable)}
          sub={overview.vat.periodFrom}
          onClick={() => onDrill({ kind: 'vat' })}
        />
      </div>

      {/* Aging + suggestions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('analytics.aging.title')}</h3>
        <AgingBar
          buckets={overview.receivables.buckets}
          total={overview.receivables.totalOutstanding}
          onSelectBucket={daysFrom => onDrill({ kind: 'customers', type: 'receivable', bucketDaysFrom: daysFrom })}
        />
      </div>

      <SuggestionStrip facts={overview.facts} onSelect={target => {
        if (target === 'customers') onDrill({ kind: 'customers', type: 'receivable' });
        else if (target === 'suppliers') onDrill({ kind: 'customers', type: 'payable' });
        else if (target === 'bank') onDrill({ kind: 'bank' });
        else if (target === 'vat') onDrill({ kind: 'vat' });
        else if (target === 'findings') onDrill({ kind: 'findings' });
      }} />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Problems panel */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <ProblemsPanel
            problems={overview.problems}
            onSelectScanner={scanner => onDrill({ kind: 'findings', scanner })}
            onSelectFinding={(finding: AnalyticsProblemEntryDto) => {
              const ids = parseEvidenceIds(finding.evidence);
              if (ids.personId) onDrill({ kind: 'customer', personId: ids.personId, personName: finding.scanner });
              else onDrill({ kind: 'findings', scanner: finding.scanner });
            }}
          />
        </div>

        {/* Top debtors */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.topDebtors')}</h3>
          <DrillTable
            rows={overview.receivables.topDebtors as unknown as Array<Record<string, unknown>>}
            columns={[
              { key: 'person', header: t('common.name'), value: r => (r.person as { name: string }).name },
              { key: 'totalOutstanding', header: t('analytics.col.total'), format: 'money' },
              { key: 'bucketOver90', header: t('analytics.aging.over90'), format: 'money' },
            ]}
            onRowClick={row => onDrill({ kind: 'customer', personId: (row.person as { id: number }).id, personName: (row.person as { name: string }).name })}
            pageSize={10}
          />
        </div>
      </div>

      {aiEnabled && <ExplainPanel view="overview" />}
    </div>
  );
}

// ─── L1: Customers (receivable/payable drill) ──────────────────────────────

interface CustomersViewProps {
  type: 'receivable' | 'payable';
  bucketDaysFrom?: number;
  filters?: FilterCondition[];
  onDrill: (view: DrillView) => void;
}

function CustomersView({ type, bucketDaysFrom, filters, onDrill }: CustomersViewProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['debt-aging', type, todayStr()],
    queryFn: () => reportsApi.debtAging({ asOf: todayStr(), type }),
    staleTime: 60_000,
  });

  const columns = useMemo(() => [
    { key: 'name', header: t('common.name'), value: (r: Record<string, unknown>) => (r.person as { name: string }).name },
    { key: 'code', header: t('common.code'), value: (r: Record<string, unknown>) => (r.person as { code: string }).code },
    { key: 'total', header: t('analytics.col.total'), format: 'money' as const },
    { key: 'current', header: t('analytics.aging.current'), format: 'money' as const },
    { key: 'b1', header: t('analytics.aging.b1'), format: 'money' as const },
    { key: 'b31', header: t('analytics.aging.b31'), format: 'money' as const },
    { key: 'b61', header: t('analytics.aging.b61'), format: 'money' as const },
    { key: 'b90', header: t('analytics.aging.over90'), format: 'money' as const },
  ], [t]);

  const rows = useMemo(() => {
    const list = (data?.personDetails ?? []).map(p => ({
      person: p.person,
      total: p.totalOutstanding,
      current: p.currentAmount,
      b1: p.bucket1to30,
      b31: p.bucket31to60,
      b61: p.bucket61to90,
      b90: p.bucketOver90,
    })) as Array<Record<string, unknown>>;
    if (bucketDaysFrom === undefined) return list;
    const field = bucketField(bucketDaysFrom);
    return list.filter(row => (row[field] as number) > 0.005);
  }, [data, bucketDaysFrom]);

  return (
    <div className="space-y-4">
      {data && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <AgingBar
            buckets={data.buckets}
            total={data.totalOutstanding}
            onSelectBucket={daysFrom => onDrill({ kind: 'customers', type, bucketDaysFrom: daysFrom })}
          />
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <DrillTable
          rows={rows}
          columns={columns}
          loading={isLoading}
          initialSort={{ key: 'total', dir: 'desc' }}
          initialFilters={filters}
          pageSize={25}
          onRowClick={row => onDrill({
            kind: 'customer',
            personId: (row.person as { id: number }).id,
            personName: (row.person as { name: string }).name,
          })}
        />
      </div>
    </div>
  );
}

// ─── L2: Customer detail ────────────────────────────────────────────────────

function CustomerView({ personId, personName, onDrill }: { personId: number; personName?: string; onDrill: (view: DrillView) => void }) {
  const { t } = useTranslation();
  const from5y = new Date(Date.now() - 5 * 365 * 864e5).toISOString().slice(0, 10);

  const { data: debt } = useQuery({
    queryKey: ['debt-report', personId],
    queryFn: () => analyticsApi.debtReport(personId, todayStr()),
    staleTime: 60_000,
  });
  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['customer-invoices', personId],
    queryFn: () => invoicesApi.list({ direction: 'Made', from: from5y, to: todayStr(), limit: 500 }),
    staleTime: 60_000,
  });
  const myInvoices = useMemo(
    () => (invoices ?? []).filter(inv => inv.personId === personId),
    [invoices],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label={t('analytics.customer.debt')} value={formatMoney(debt?.endBalance ?? 0)} tone={(debt?.endBalance ?? 0) > 0 ? 'warning' : 'positive'} />
        <MetricTile label={t('analytics.customer.turnoverDebit')} value={formatMoney(debt?.debitTurnover ?? 0)} />
        <MetricTile label={t('analytics.customer.turnoverCredit')} value={formatMoney(debt?.creditTurnover ?? 0)} />
        <MetricTile label={t('analytics.customer.invoices')} value={String(myInvoices.length)} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.customer.invoicesList')}</h3>
        <DrillTable
          rows={myInvoices as unknown as Array<Record<string, unknown>>}
          columns={[
            { key: 'date', header: t('common.date'), format: 'date' },
            { key: 'documentNumber', header: t('analytics.col.docNo') },
            { key: 'content', header: t('analytics.col.content') },
            { key: 'totalSum', header: t('analytics.col.total'), format: 'money' },
          ]}
          loading={invoicesLoading}
          initialSort={{ key: 'date', dir: 'desc' }}
          pageSize={15}
          emptyText={t('analytics.customer.noInvoices')}
          onRowClick={row => onDrill({ kind: 'invoice', invoiceId: row.id as number })}
        />
      </div>

      <ExplainPanel view="customer" entityId={personId} title={personName} />
    </div>
  );
}

// ─── L2: Invoice detail + possible payments ────────────────────────────────

function InvoiceView({ invoiceId }: { invoiceId: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => invoicesApi.getById(invoiceId),
  });
  const { data: matches, isLoading: matchesLoading } = useQuery({
    queryKey: ['matching-payments', invoiceId],
    queryFn: () => analyticsApi.findMatchingPayments(invoiceId),
    enabled: !!invoice,
    staleTime: 60_000,
  });

  return (
    <div className="space-y-4">
      {invoice && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricTile label={t('analytics.col.docNo')} value={invoice.documentNumber} />
          <MetricTile label={t('common.date')} value={invoice.date?.slice(0, 10) ?? ''} />
          <MetricTile label={t('analytics.col.total')} value={formatMoney(invoice.totalSum)} />
          <MetricTile label={t('analytics.invoice.openInApp')} value="↗" onClick={() => navigate(`/invoices/${invoiceId}`)} />
        </div>
      )}
      {isLoading && <div className="py-8 text-center text-gray-400">{t('analytics.loading')}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('analytics.invoice.matchesTitle')}</h3>
        {matchesLoading && <p className="text-sm text-gray-400 py-2">{t('analytics.table.loading')}</p>}
        {!matchesLoading && (!matches || matches.length === 0) && (
          <p className="text-sm text-gray-400 py-2">{t('analytics.invoice.noMatches')}</p>
        )}
        {matches && matches.length > 0 && (
          <DrillTable
            rows={matches as unknown as Array<Record<string, unknown>>}
            columns={[
              { key: 'date', header: t('common.date'), format: 'date' },
              { key: 'documentNumber', header: t('analytics.col.docNo') },
              { key: 'personName', header: t('common.name') },
              { key: 'content', header: t('analytics.col.content') },
              { key: 'amount', header: t('analytics.col.amount'), format: 'money' },
              { key: 'amountDifference', header: t('analytics.invoice.delta'), format: 'money' },
            ]}
            initialSort={{ key: 'date', dir: 'desc' }}
            pageSize={10}
          />
        )}
      </div>

      <ExplainPanel view="invoice" entityId={invoiceId} />
    </div>
  );
}

// ─── L1: Findings (decision log / scanner results) ─────────────────────────

function FindingsView({ scanner, onDrill }: { scanner?: string; onDrill: (view: DrillView) => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['findings', scanner ?? 'all'],
    queryFn: () => analyticsApi.findings({ take: 100 }),
    staleTime: 30_000,
  });

  const rows = useMemo(() => {
    let list = (data?.items ?? []).map(e => ({
      timestamp: e.timestamp,
      scanner: e.agentName,
      title: e.outcome,
      evidence: e.evidence,
    }));
    if (scanner) list = list.filter(r => r.scanner.toLowerCase() === scanner.toLowerCase());
    return list as Array<Record<string, unknown>>;
  }, [data, scanner]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <DrillTable
        rows={rows}
        columns={[
          { key: 'timestamp', header: t('analytics.col.time'), format: 'date' },
          { key: 'scanner', header: t('analytics.col.scanner') },
          { key: 'title', header: t('analytics.col.title') },
        ]}
        loading={isLoading}
        initialSort={{ key: 'timestamp', dir: 'desc' }}
        pageSize={25}
        onRowClick={row => {
          const ids = parseEvidenceIds(String(row.evidence ?? ''));
          if (ids.personId) onDrill({ kind: 'customer', personId: ids.personId });
          else if (ids.invoiceId) onDrill({ kind: 'invoice', invoiceId: ids.invoiceId });
        }}
      />
    </div>
  );
}

// ─── L1: Bank / VAT (shallow views over overview data) ──────────────────────

function BankView({ overviewLoading, onOpenBanking }: { overviewLoading: boolean; onOpenBanking: () => void }) {
  const { t } = useTranslation();
  const { data: overview } = useQuery({ queryKey: ['analytics-overview', getWorkspaceId()], queryFn: analyticsApi.overview, staleTime: 60_000 });

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">{t('analytics.tile.cash')}</h3>
        <button onClick={onOpenBanking} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
          {t('analytics.invoice.openInApp')} →
        </button>
      </div>
      <DrillTable
        rows={(overview?.cash.accounts ?? []) as unknown as Array<Record<string, unknown>>}
        columns={[
          { key: 'accountNumber', header: t('analytics.col.account') },
          { key: 'accountName', header: t('common.name') },
          { key: 'balance', header: t('analytics.col.balance'), format: 'money' },
        ]}
        loading={overviewLoading}
        pageSize={10}
      />
    </div>
  );
}

function VatView({ onOpenVat }: { onOpenVat: () => void }) {
  const { t } = useTranslation();
  const { data: overview } = useQuery({ queryKey: ['analytics-overview', getWorkspaceId()], queryFn: analyticsApi.overview, staleTime: 60_000 });
  const vat = overview?.vat;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <MetricTile label={t('analytics.vat.salesVat')} value={formatMoney(vat?.salesVat ?? 0)} />
        <MetricTile label={t('analytics.vat.purchaseVat')} value={formatMoney(vat?.purchaseVat ?? 0)} />
        <MetricTile label={t('analytics.vat.net')} value={formatMoney(vat?.netVatPayable ?? 0)} tone={(vat?.netVatPayable ?? 0) > 0 ? 'warning' : 'positive'} />
      </div>
      <button onClick={onOpenVat} className="text-sm text-primary-600 hover:text-primary-800 font-medium">
        {t('analytics.vat.openFull')} →
      </button>
    </div>
  );
}
